import { Apollo, gql } from 'apollo-angular';
import { of, Subject, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import {
  AddManualPayment,
  AddNoteToOrder,
  AddNoteToOrderInput,
  CancelOrder,
  CancelOrderInput,
  CreateFulfillment,
  DeleteOrderNote,
  FulfillOrderInput,
  GetOrder,
  GetOrderHistory,
  GetOrderList,
  GetOrderSummary,
  HistoryEntryListOptions,
  ManualPaymentInput,
  ModifyOrder,
  ModifyOrderInput,
  OrderListOptions,
  RefundOrder,
  RefundOrderInput,
  SettlePayment,
  SettleRefund,
  SettleRefundInput,
  TransitionFulfillmentToState,
  TransitionOrderToState,
  TransitionPaymentToState,
  UpdateOrderCustomFields,
  UpdateOrderInput,
  UpdateOrderNote,
  UpdateOrderNoteInput,
} from '../../common/generated-types';
import { awaitPipe } from '../../shared/utils/utils';
import {
  ADD_MANUAL_PAYMENT_TO_ORDER,
  ADD_NOTE_TO_ORDER,
  CANCEL_ORDER,
  CREATE_FULFILLMENT,
  DELETE_ORDER_NOTE,
  GET_ORDER,
  GET_ORDERS_LIST,
  GET_ORDER_HISTORY,
  GET_ORDER_SUMMARY,
  MODIFY_ORDER,
  REFUND_ORDER,
  SETTLE_PAYMENT,
  SETTLE_REFUND,
  TRANSITION_FULFILLMENT_TO_STATE,
  TRANSITION_ORDER_TO_STATE,
  TRANSITION_PAYMENT_TO_STATE,
  UPDATE_ORDER_CUSTOM_FIELDS,
  UPDATE_ORDER_NOTE,
} from '../definitions/order-definitions';

import { BaseDataService } from './base-data.service';
import { QueryResult } from '../query-result';
import { ID } from '@vendure/common/lib/shared-types';

export class OrderDataService {
  constructor(private baseDataService: BaseDataService, private apollo: Apollo) {}

  getOrders(options: OrderListOptions = { take: 10 }) {
    return this.baseDataService.query<GetOrderList.Query, GetOrderList.Variables>(GET_ORDERS_LIST, {
      options,
    });
  }

  syncToLs(orderId: string): Observable<boolean> {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation requestBacksync($orderId: Int!) {
            requestBacksync(orderId: $orderId)
          }
        `,
        variables: { orderId: Number(orderId) },
      })
      .pipe(map((payload: any) => payload.data.requestBacksync as boolean));
  }

  forceSyncToLS(orderId: string): Observable<boolean> {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation forceSync($orderId: ID!) {
            forceSync(orderId: $orderId)
          }
        `,
        variables: { orderId: Number(orderId) },
      })
      .pipe(map((payload: any) => payload.data.forceSync as boolean));
  }

  getOrdersPaginated(options: any) {
    const query = this.apollo.watchQuery({
      query: gql`
        query GetOrdersPaginated($options: OrderPaginationOptions) {
          ordersPaginated(options: $options) {
            items {
              id
              active
              code
              state
              totalWithTax
              total
              orderPlacedAt
              shipping
              shippingWithTax
              updatedAt
              customFields {
                syncedToLS
              }
              customer {
                id
                firstName
                lastName
              }
              shippingAddress {
                streetLine1
                streetLine2
                city
                customFields {
                  isStore
                  deliveryDay
                }
              }

              shippingLines {
                shippingMethod {
                  id
                  createdAt
                  updatedAt
                  code
                  name
                  description
                  translations {
                    name
                    description
                  }
                }
                price
                priceWithTax
              }

              channels {
                id
                token
                code
              }
            }
            total
          }
        }
      `,
      variables: { options },
      fetchPolicy: 'no-cache',
    });
    return {
      ref: query,
      stream$: query.valueChanges,
      completed$: new Subject<boolean>(),
    };
  }

  getOrder(id: string) {
    return this.baseDataService.query<GetOrder.Query, GetOrder.Variables>(GET_ORDER, { id });
    // const query = this.apollo.watchQuery({ query: GET_ORDER, variables: { id } });
    // return new QueryResult(query, this.apollo);
  }

  getOrderHistory(id: string, options?: HistoryEntryListOptions) {
    return this.baseDataService.query<GetOrderHistory.Query, GetOrderHistory.Variables>(GET_ORDER_HISTORY, {
      id,
      options,
    });
  }

  settlePayment(id: string) {
    return this.baseDataService.mutate<SettlePayment.Mutation, SettlePayment.Variables>(SETTLE_PAYMENT, {
      id,
    });
  }

  transitionPaymentToState(id: string, state: string) {
    return this.baseDataService.mutate<TransitionPaymentToState.Mutation, TransitionPaymentToState.Variables>(TRANSITION_PAYMENT_TO_STATE, {
      id,
      state,
    });
  }

  createFulfillment(input: FulfillOrderInput) {
    return this.baseDataService.mutate<CreateFulfillment.Mutation, CreateFulfillment.Variables>(CREATE_FULFILLMENT, {
      input,
    });
  }

  transitionFulfillmentToState(id: string, state: string) {
    if (state == 'Delivered') {
      return this.apollo.mutate({
        mutation: gql`
          mutation MarkAsDelivered($id: ID!) {
            markFulfilmentAsDelivered(id: $id) {
              __typename
            }
          }
        `,
        variables: { id },
      });
    }
    return this.baseDataService.mutate<TransitionFulfillmentToState.Mutation, TransitionFulfillmentToState.Variables>(TRANSITION_FULFILLMENT_TO_STATE, {
      id,
      state,
    });
  }

  cancelOrder(input: CancelOrderInput) {
    return this.baseDataService.mutate<CancelOrder.Mutation, CancelOrder.Variables>(CANCEL_ORDER, {
      input,
    });
  }

  refundOrder(input: RefundOrderInput) {
    return this.baseDataService.mutate<RefundOrder.Mutation, RefundOrder.Variables>(REFUND_ORDER, {
      input,
    });
  }

  settleRefund(input: SettleRefundInput, orderId: string) {
    return this.baseDataService.mutate<SettleRefund.Mutation, SettleRefund.Variables>(SETTLE_REFUND, {
      input,
    });
  }

  addNoteToOrder(input: AddNoteToOrderInput) {
    return this.baseDataService.mutate<AddNoteToOrder.Mutation, AddNoteToOrder.Variables>(ADD_NOTE_TO_ORDER, {
      input,
    });
  }

  updateOrderNote(input: UpdateOrderNoteInput) {
    return this.baseDataService.mutate<UpdateOrderNote.Mutation, UpdateOrderNote.Variables>(UPDATE_ORDER_NOTE, {
      input,
    });
  }

  deleteOrderNote(id: string) {
    return this.baseDataService.mutate<DeleteOrderNote.Mutation, DeleteOrderNote.Variables>(DELETE_ORDER_NOTE, {
      id,
    });
  }

  transitionToState(id: string, state: string, force = false) {
    return this.baseDataService.mutate<TransitionOrderToState.Mutation, TransitionOrderToState.Variables>(TRANSITION_ORDER_TO_STATE, {
      id,
      state,
      force,
    });
  }

  updateOrderCustomFields(input: UpdateOrderInput) {
    return this.baseDataService.mutate<UpdateOrderCustomFields.Mutation, UpdateOrderCustomFields.Variables>(UPDATE_ORDER_CUSTOM_FIELDS, {
      input,
    });
  }

  getOrderSummary(start: Date, end: Date) {
    return this.baseDataService.query<GetOrderSummary.Query, GetOrderSummary.Variables>(GET_ORDER_SUMMARY, {
      start: start.toISOString(),
      end: end.toISOString(),
    });
  }

  modifyOrder(input: ModifyOrderInput) {
    return this.baseDataService.mutate<ModifyOrder.Mutation, ModifyOrder.Variables>(MODIFY_ORDER, {
      input,
    });
  }

  modifyOrderCustom(input: ModifyOrderInput): any {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation modifyOrderCustom($input: JSON!) {
            modifyOrderCustom(input: $input)
          }
        `,
        variables: { input },
      })
      .pipe(map((payload: any) => payload?.data?.modifyOrderCustom));
  }

  setSpecialBuyEstimation(orderId: ID, data: { deliveryPrice: number; daysFrom: number; daysTo: number }): Observable<boolean> {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation setSpecialBuyOrderCosts($orderId: ID!, $deliveryPrice: Int, $daysFrom: Int, $daysTo: Int) {
            setSpecialBuyOrderCosts(orderId: $orderId, deliveryPrice: $deliveryPrice, daysFrom: $daysFrom, daysTo: $daysTo)
          }
        `,
        variables: { orderId, ...data },
      })
      .pipe(map((payload: any) => payload?.data?.modifyOrderCustom));
  }

  customCancelOrder(order: any) {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation customCancelOrder($order: JSON!) {
            customCancelOrder(order: $order)
          }
        `,
        variables: { order },
      })
      .pipe(map((payload: any) => payload.data.customCancelOrder));
  }

  addManualPaymentToOrder(input: ManualPaymentInput) {
    return this.baseDataService.mutate<AddManualPayment.Mutation, AddManualPayment.Variables>(ADD_MANUAL_PAYMENT_TO_ORDER, { input });
  }

  customAddManualPaymentToOrder(input: ManualPaymentInput) {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation customAddManualPaymentToOrder($input: JSON) {
            customAddManualPaymentToOrder(input: $input)
          }
        `,
        variables: { input },
      })
      .pipe(map((payload: any) => payload.data.customAddManualPaymentToOrder));
  }

  getOperators(search: string) {
    return this.apollo
      .query({
        query: gql`
          query GetOperators($options: PaginationOptions) {
            getOperators(options: $options) {
              items {
                firstName
                lastName
                emailAddress
                user {
                  id
                }
              }
            }
          }
        `,
        variables: { options: { take: 10, search } },
      })
      .pipe(map((payload: any) => payload.data.getOperators));
  }

  asssignOperator(orderId, operatorId) {
    return this.apollo.mutate({
      mutation: gql`
        mutation AssignOperator($operatorId: ID!, $orderId: ID!) {
          assignOperator(orderId: $orderId, operatorId: $operatorId)
        }
      `,
      variables: { orderId, operatorId },
    });
  }

  customCreatePaymentLink(input: ManualPaymentInput) {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation customCreatePaymentLink($input: JSON) {
            customCreatePaymentLink(input: $input)
          }
        `,
        variables: { input },
      })
      .pipe(map((payload: any) => payload.data.customCreatePaymentLink));
  }
}

const ORDER_FIELDS = `
  id
  active
  code
  state
  totalWithTax
  total
  orderPlacedAt
  shipping
  shippingWithTax
  surcharges{
    id
    price
    priceWithTax
  }
  payments{
    state
    metadata
  }
  shippingAddress{
    streetLine1
     streetLine2
     city
     customFields{
       isStore
       deliveryDay
     }
    }
  lines {
    id
    unitPrice
    unitPriceWithTax
    quantity
    linePrice
    linePriceWithTax
    taxRate
    featuredAsset {
      preview
      customFields {
        isActive
      }
    }
    productVariant {
      product {
        name
        slug
        collections {
          name
        }
        customFields {
          capacity
        }
      }
      id
        name
        stockLevel
        availableStock
    }
    featuredAsset {
      preview
    }
  }
  customFields{
    selectedCurrency
    currencyRate
  }
`;
